import SunAccent from "@/components/feature/SunAccent";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { usePublicExperiences } from "@/hooks/usePublicCatalog";

export default function ExperiencesSection() {
  const { data: items, loading } = usePublicExperiences();
  const experiences = items.map((item) => item.summary);

  return (
    <section id="experiences" className="sun-glaze bg-background-100 py-20 md:py-28">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              What You Can Do
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl leading-tight text-foreground-950 font-semibold">
            Our Experiences
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
            Curated farm and village experiences for every kind of curious traveler — from a
            single day on the farm to a full immersion in rural life.
          </p>
        </Reveal>

        {loading && experiences.length === 0 ? (
          <div className="py-16 flex flex-col items-center gap-4 text-foreground-600">
            <span className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></span>
            <p className="text-sm">Loading experiences…</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {experiences.map((exp, index) => (
              <Reveal key={exp.slug} delay={(index % 4) * 80}>
                <Link
                  to={`/experiences/${exp.slug}`}
                  className="group h-full bg-background-50 rounded-lg border border-background-200 p-6 flex flex-col hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <span className="text-3xl mb-4 block">{exp.emoji}</span>
                  <h3 className="font-heading text-xl text-foreground-950 font-semibold leading-snug">
                    {exp.title}
                  </h3>
                  <p className="text-xs font-semibold uppercase tracking-wider text-accent-600 mt-2">
                    {exp.tagline}
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mt-4 flex-1">
                    {exp.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mt-5">
                    {exp.highlights.map((h) => (
                      <span
                        key={h}
                        className="text-[11px] font-medium bg-secondary-100 text-secondary-800 px-2.5 py-1 rounded-full"
                      >
                        {h}
                      </span>
                    ))}
                  </div>

                  <div className="mt-5 pt-4 border-t border-background-200 flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2">
                      <i className="ri-user-follow-line text-primary-500"></i>
                      <span className="text-xs font-medium text-foreground-700">{exp.perfectFor}</span>
                    </span>
                    <i className="ri-arrow-right-line text-primary-600 transition-transform group-hover:translate-x-1"></i>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal className="mt-12 text-center" delay={80}>
          <Link
            to="/experiences"
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm md:text-base font-semibold px-8 py-4 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Browse All Experiences
            <i className="ri-filter-3-line"></i>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}